import React, { useState, useEffect } from "react";
import axiosInstance, { serverURL } from "../../services/FetchNodeServices";
import { toast } from "react-toastify";

const DownloadImages = () => {
  const [info, setInfo] = useState(null);
  const [images, setImages] = useState([]);
  const [totalImages, setTotalImages] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [activeTab, setActiveTab] = useState("browse"); // browse | download

  useEffect(() => { fetchInfo(); }, []);
  useEffect(() => { fetchImages(); }, [currentPage, search]);

  const fetchInfo = async () => {
    try {
      const res = await axiosInstance.get("/api/v1/product/images-info");
      setInfo(res.data);
    } catch (error) {
      toast.error("Failed to load images info.");
    } finally {
      setLoading(false);
    }
  };

  const fetchImages = async () => {
    try {
      setImagesLoading(true);
      const res = await axiosInstance.get(
        `/api/v1/product/images-list?page=${currentPage}&limit=50&search=${search}`
      );
      setImages(res.data.images);
      setTotalImages(res.data.totalImages);
      setTotalPages(res.data.totalPages);
    } catch (error) {
      toast.error("Failed to load images.");
    } finally {
      setImagesLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setCurrentPage(1);
  };

  const handleDownload = (batch) => {
    setDownloading(batch.batch);
    toast.info(`⏳ Preparing ${batch.filename}...`, { autoClose: 3000 });
    const link = document.createElement("a");
    link.href = `${serverURL}/api/v1/product/download-images?batch=${batch.batch}`;
    link.setAttribute("download", batch.filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => {
      setDownloading(null);
      toast.success(`✅ Download started: ${batch.filename}`);
    }, 2000);
  };

  if (loading) {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border text-primary" />
        <p className="mt-3">Loading...</p>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <div className="card shadow">
        <div className="card-body">
          <h4 className="card-title mb-4">🖼️ Server Images Manager</h4>

          {/* Stats */}
          <div className="row mb-4">
            <div className="col-md-4 mb-2">
              <div style={{ background: "#d4edda", border: "1px solid #28a745", borderRadius: "8px", padding: "15px", textAlign: "center" }}>
                <h2 style={{ color: "#155724", margin: 0 }}>{info?.totalImages?.toLocaleString()}</h2>
                <p style={{ color: "#155724", margin: 0 }}>Total Images</p>
              </div>
            </div>
            <div className="col-md-4 mb-2">
              <div style={{ background: "#cce5ff", border: "1px solid #004085", borderRadius: "8px", padding: "15px", textAlign: "center" }}>
                <h2 style={{ color: "#004085", margin: 0 }}>{info?.totalBatches}</h2>
                <p style={{ color: "#004085", margin: 0 }}>Download Batches</p>
              </div>
            </div>
            <div className="col-md-4 mb-2">
              <div style={{ background: "#fff3cd", border: "1px solid #856404", borderRadius: "8px", padding: "15px", textAlign: "center" }}>
                <h2 style={{ color: "#856404", margin: 0 }}>{info?.batchSize?.toLocaleString() || "20,000"}</h2>
                <p style={{ color: "#856404", margin: 0 }}>Images per Batch</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <ul className="nav nav-tabs mb-4">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "browse" ? "active" : ""}`}
                onClick={() => setActiveTab("browse")}
              >
                🖼️ Browse Images
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "download" ? "active" : ""}`}
                onClick={() => setActiveTab("download")}
              >
                📥 Download Batches
              </button>
            </li>
          </ul>

          {/* Browse Tab */}
          {activeTab === "browse" && (
            <div>
              {/* Search */}
              <form onSubmit={handleSearch} className="d-flex gap-2 mb-4">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by filename (e.g. 9780023487507)"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                <button type="submit" className="btn btn-primary">Search</button>
                {search && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => { setSearch(""); setSearchInput(""); setCurrentPage(1); }}
                  >
                    Clear
                  </button>
                )}
              </form>

              {/* Results count */}
              <p className="text-muted mb-3">
                Showing <strong>{images.length}</strong> of <strong>{totalImages.toLocaleString()}</strong> images
                {search && ` matching "${search}"`}
              </p>

              {/* Image Grid */}
              {imagesLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" />
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: "10px", marginBottom: "20px" }}>
                  {images.map((filename) => (
                    <div key={filename} style={{ border: "1px solid #ddd", borderRadius: "8px", overflow: "hidden", textAlign: "center" }}>
                      <img
                        src={`${serverURL}/public/image/${filename}`}
                        alt={filename}
                        style={{ width: "100%", height: "100px", objectFit: "cover" }}
                        onError={(e) => { e.target.src = "/no-image-placeholder.png"; }}
                      />
                      <p style={{ fontSize: "10px", padding: "4px", margin: 0, wordBreak: "break-all", color: "#555" }}>
                        {filename}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(1)}
                  >«</button>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(p => p - 1)}
                  >‹</button>
                  <span className="px-3">
                    Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                  </span>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(p => p + 1)}
                  >›</button>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                  >»</button>
                </div>
              )}
            </div>
          )}

          {/* Download Tab */}
          {activeTab === "download" && (
            <div>
              <div style={{ background: "#e8f4fd", border: "1px solid #3498db", borderRadius: "8px", padding: "15px", marginBottom: "20px" }}>
                <p style={{ fontWeight: "bold", color: "#2980b9", marginBottom: "8px" }}>ℹ️ How downloading works:</p>
                <ul style={{ fontSize: "13px", color: "#555", paddingLeft: "20px", marginBottom: "0" }}>
                  <li>📦 Images split into batches of <strong>{info?.batchSize?.toLocaleString() || "20,000"}</strong> per ZIP</li>
                  <li>⏳ Each ZIP takes <strong>2-5 minutes</strong> to prepare</li>
                  <li>💾 Download <strong>one batch at a time</strong> for best results</li>
                </ul>
              </div>

              <div className="table-responsive">
                <table className="table table-bordered table-hover">
                  <thead className="table-dark">
                    <tr>
                      <th>Batch</th>
                      <th>Range</th>
                      <th>Count</th>
                      <th>Filename</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {info?.batches?.map((batch) => (
                      <tr key={batch.batch}>
                        <td><strong>#{batch.batch}</strong></td>
                        <td>{batch.start.toLocaleString()} - {batch.end.toLocaleString()}</td>
                        <td>{batch.count.toLocaleString()}</td>
                        <td><code>{batch.filename}</code></td>
                        <td>
                          <button
                            onClick={() => handleDownload(batch)}
                            disabled={downloading === batch.batch}
                            className="btn btn-sm btn-primary"
                          >
                            {downloading === batch.batch ? (
                              <><span className="spinner-border spinner-border-sm me-1" />Preparing...</>
                            ) : "📥 Download"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DownloadImages;
